import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import { EASE } from "../../lib/motion";

/**
 * CinematicHero — the shared cinematic hero pattern used by every major page.
 *
 * Five-layer editorial motion:
 *   1. Base image — Ken Burns 80s (scale 1 → 1.06 + slow pan), cool-graded (-4deg hue).
 *   2. Atmospheric drift — 45s low-opacity navy+gold gradient panning across the frame.
 *   3. Scroll parallax — image translates upward at 0.4x scrollY, spring-damped.
 *   4. Scroll cue — brass hairline + descending gold dot (2.5s), fades out past ~10% viewport.
 *   5. Bottom fade — 120px gradient from transparent to the next section's tone.
 *      Set `fadeTone="dark"` for pages whose next section is on the navy surface (Insights),
 *      `"light"` (default) for pages that continue on the cool body surface,
 *      `"none"` to disable the fade (rare — e.g. hero flows into a hard-edged block).
 *
 * All motion collapses under `prefers-reduced-motion: reduce`.
 *
 * Consumers pass an overlay via `children`; use `HeroOverlay` for the standard editorial
 * eyebrow + serif title + optional statement block. Home / Firm may pass custom overlays
 * where the typography requires special treatment (italic gold, multi-line titles).
 */
export const CinematicHero = ({
  image,
  imageAlt = "",
  eager = false,
  fadeTone = "light",
  children,
  "data-testid": testId = "cinematic-hero",
}) => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);

  const { scrollY } = useScroll();
  const rawParallax = useTransform(scrollY, (v) => -v * 0.4);
  const parallaxY = useSpring(rawParallax, {
    stiffness: 90,
    damping: 24,
    restDelta: 0.5,
    mass: 0.6,
  });
  const parallaxSafe = reduce ? 0 : parallaxY;

  const cueOpacity = useTransform(scrollY, (v) => {
    if (typeof window === "undefined") return 1;
    const fadeEnd = window.innerHeight * 0.1;
    if (v <= 0) return 1;
    if (v >= fadeEnd) return 0;
    return 1 - v / fadeEnd;
  });

  const fadeGradient =
    fadeTone === "dark"
      ? "linear-gradient(180deg, rgba(11,19,43,0) 0%, rgba(11,19,43,0.55) 55%, var(--q-bg-dark) 100%)"
      : fadeTone === "none"
      ? "transparent"
      : "linear-gradient(180deg, rgba(247,248,250,0) 0%, rgba(247,248,250,0.55) 55%, var(--q-body) 100%)";

  return (
    <section
      ref={sectionRef}
      data-testid={testId}
      data-section-tone="dark"
      data-cursor-scheme="dark"
      className="relative w-full min-h-[80vh] md:min-h-[85vh] overflow-hidden bg-navy"
    >
      <motion.img
        src={image}
        alt={imageAlt}
        loading={eager ? "eager" : "lazy"}
        fetchpriority={eager ? "high" : "auto"}
        decoding={eager ? "sync" : "async"}
        className="q-hero-cool absolute inset-0 h-[112%] w-full object-cover"
        initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.0, x: "-1.5%", y: "-1.5%" }}
        animate={
          reduce
            ? { opacity: 1 }
            : {
                opacity: 1,
                scale: [1.0, 1.06, 1.0],
                x: ["-1.5%", "0%", "-1.5%"],
                y: ["-1.5%", "0%", "-1.5%"],
              }
        }
        transition={
          reduce
            ? { duration: 0 }
            : {
                opacity: { duration: 1.6, ease: EASE, delay: 0.6 },
                scale: { duration: 80, repeat: Infinity, ease: "easeInOut" },
                x: { duration: 80, repeat: Infinity, ease: "easeInOut" },
                y: { duration: 80, repeat: Infinity, ease: "easeInOut" },
              }
        }
        style={{
          y: parallaxSafe,
          top: "-6%",
          willChange: reduce ? "auto" : "transform, opacity",
        }}
      />

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-navy/[0.08]" />

      {!reduce && (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            top: "-10%",
            left: "-25%",
            right: "-25%",
            bottom: "-10%",
            background:
              "radial-gradient(ellipse 60% 55% at 30% 40%, rgba(11,19,43,0.14) 0%, transparent 60%), radial-gradient(ellipse 45% 40% at 75% 60%, rgba(184,162,106,0.09) 0%, transparent 65%)",
            mixBlendMode: "soft-light",
            willChange: "transform, opacity",
          }}
          initial={{ opacity: 0.06, x: "-3%" }}
          animate={{ opacity: [0.06, 0.1, 0.06], x: ["-3%", "3%", "-3%"] }}
          transition={{ duration: 45, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, transparent 45%, rgba(11,19,43,0.30) 72%, rgba(11,19,43,0.50) 100%)",
        }}
      />

      <div
        className="relative min-h-[80vh] md:min-h-[85vh] flex flex-col justify-end"
        style={{
          padding:
            "clamp(96px, 12vh, 160px) clamp(24px, 5vw, 96px) clamp(88px, 11vh, 128px) clamp(24px, 5vw, 128px)",
        }}
      >
        <div className="max-w-4xl">{children}</div>
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 right-0 bottom-0"
        style={{ height: 120, background: fadeGradient }}
      />

      <motion.div
        aria-hidden="true"
        data-testid="hero-scroll-cue"
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-10"
        style={{
          bottom: "clamp(28px, 4vh, 48px)",
          opacity: reduce ? 0.7 : cueOpacity,
        }}
      >
        <div className="relative" style={{ width: 1, height: 48 }}>
          <span className="absolute inset-0 block bg-gold" style={{ opacity: 0.8 }} />
          <span
            className="q-hero-cue-dot absolute left-1/2 top-0 block rounded-full bg-gold"
            style={{ width: 4, height: 4, transform: "translate(-50%, 0)" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

/**
 * HeroOverlay — the standard editorial overlay used by most pages.
 * Renders: gold hairline + tracked eyebrow, then a serif title (single or multi-line),
 * then an optional supporting statement (italic serif or sans body).
 *
 * Sizes tuned in Phase 3 so titles fit cleanly on 375px viewports:
 *   title:  clamp(40px, 7.4vw, 116px)
 *   short:  clamp(44px, 7vw,   108px)  — for shorter titles like "The Firm."
 */
export const HeroOverlay = ({
  eyebrow,
  title,
  statement,
  statementSerif = true,
  size = "default",
  eyebrowDelay = 1.4,
  titleDelay = 1.58,
  statementDelay = 1.86,
}) => {
  const reduce = useReducedMotion();
  const lines = Array.isArray(title) ? title : [title];
  const t = {
    eyebrow: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: eyebrowDelay },
    line: (i) =>
      reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: titleDelay + i * 0.14 },
    statement: reduce
      ? { duration: 0 }
      : { duration: 1.0, ease: EASE, delay: statementDelay + (lines.length - 1) * 0.14 },
  };
  const titleFontSize =
    size === "short"
      ? "clamp(44px, 7vw, 108px)"
      : "clamp(40px, 7.4vw, 116px)";
  return (
    <>
      {eyebrow ? (
        <motion.div
          className="flex items-center gap-4"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={t.eyebrow}
        >
          <span aria-hidden="true" className="block h-px bg-gold" style={{ width: 24 }} />
          <span
            className="font-sans uppercase text-gold"
            style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
          >
            {eyebrow}
          </span>
        </motion.div>
      ) : null}

      <h1
        data-testid="hero-title"
        className="mt-8 font-serif font-normal text-offwhite text-balance"
        style={{
          fontSize: titleFontSize,
          lineHeight: 1.02,
          letterSpacing: "-0.02em",
          textShadow: "0 1px 24px rgba(11,19,43,0.35)",
        }}
      >
        {lines.map((line, i) => (
          <motion.span
            key={i}
            className="block"
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={t.line(i)}
          >
            {line}
          </motion.span>
        ))}
      </h1>

      {statement ? (
        <motion.p
          data-testid="hero-statement"
          className={
            statementSerif
              ? "mt-8 max-w-[52ch] font-serif italic text-offwhite/90"
              : "mt-8 max-w-[56ch] font-sans text-offwhite/90"
          }
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={t.statement}
          style={
            statementSerif
              ? {
                  fontSize: "clamp(19px, 1.85vw, 27px)",
                  lineHeight: 1.35,
                  textShadow: "0 1px 16px rgba(11,19,43,0.30)",
                }
              : {
                  fontSize: "clamp(16px, 1.2vw, 18px)",
                  lineHeight: 1.7,
                  textShadow: "0 1px 16px rgba(11,19,43,0.30)",
                }
          }
        >
          {statement}
        </motion.p>
      ) : null}
    </>
  );
};

export default CinematicHero;
